import {
  useMissionControlNetworks,
  type CustomerNetworkProjection,
} from '../hooks/useMissionControlNetworks';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { formatDateTime } from '../lib/formatters';
import { Freshness } from './Freshness';
import { SkeletonRow } from './Skeleton';
import { StatusBadge } from './StatusBadge';

const healthKey: Record<CustomerNetworkProjection['health'], MessageKey> = {
  healthy: 'location.monitoring.healthy',
  degraded: 'location.monitoring.degraded',
  offline: 'location.monitoring.offline',
  unknown: 'location.monitoring.unknown',
};
const coverageKey: Record<CustomerNetworkProjection['coverage'], MessageKey> = {
  complete: 'location.monitoring.complete',
  partial: 'location.monitoring.partial',
  none: 'location.monitoring.none',
};
const records = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === 'object' && !Array.isArray(item),
      )
    : [];
const string = (value: unknown) => (typeof value === 'string' ? value : '');

export function MissionControlNetworks({ locationId }: { locationId: string }) {
  const { t } = useI18n();
  const query = useMissionControlNetworks(locationId);
  return (
    <section className="space-y-3" aria-labelledby="portal-monitoring-heading">
      <div>
        <h2 id="portal-monitoring-heading" className="font-semibold text-text">
          {t('location.monitoring.title')}
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          {t('location.monitoring.hint')}
        </p>
      </div>
      {query.isLoading ? (
        <SkeletonRow />
      ) : query.isError && !query.data ? (
        <p
          role="alert"
          className="rounded-lg border border-border bg-surface p-4 text-sm text-notification"
        >
          {t('location.monitoring.error')}{' '}
          <button
            className="min-h-11 underline"
            type="button"
            onClick={() => void query.refetch()}
          >
            {t('common.retry')}
          </button>
        </p>
      ) : !query.data?.length ? (
        <p className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
          {t('location.monitoring.empty')}
        </p>
      ) : (
        query.data.map((network) => {
          const sources = records(network.sources);
          const refreshWindows = sources
            .map((source) => Number(source.stale_after_seconds) * 1000)
            .filter((window) => Number.isFinite(window) && window > 0);
          const staleMs = refreshWindows.length
            ? Math.min(...refreshWindows)
            : 300_000;
          const devices = records(network.devices);
          const incidents = records(network.incidents);
          return (
            <article
              key={network.id}
              className="rounded-lg border border-border bg-surface p-4 sm:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-text">
                    {network.network_name}
                  </h3>
                  <p className="mt-1 text-xs text-text-muted">
                    {t(
                      coverageKey[network.coverage] ??
                        'location.monitoring.none',
                    )}
                  </p>
                </div>
                <Freshness
                  label={t('location.monitoring.source')}
                  lastUpdatedAt={network.observed_at}
                  expectedRefreshMs={staleMs}
                  staleMs={staleMs * 2}
                  forcedState={
                    query.isError
                      ? 'offline'
                      : network.is_stale
                        ? 'stale'
                        : undefined
                  }
                />
              </div>
              <p className="mt-3 text-sm text-text">
                {t('location.monitoring.last_reported', {
                  status: t(
                    healthKey[network.health] ?? 'location.monitoring.unknown',
                  ),
                })}
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <Count
                  title={t('location.monitoring.devices')}
                  value={network.devices_total}
                />
                <Count
                  title={t('location.monitoring.online')}
                  value={network.devices_online}
                />
                <Count
                  title={t('location.monitoring.offline')}
                  value={network.devices_offline}
                />
                <Count
                  title={t('location.monitoring.alerts')}
                  value={network.active_alerts}
                />
              </dl>
              {network.devices_unknown > 0 && (
                <p className="mt-2 text-xs text-text-muted">
                  {t('location.monitoring.unknown_devices', {
                    n: network.devices_unknown,
                  })}
                </p>
              )}
              <details className="mt-4 border-t border-border pt-3">
                <summary className="min-h-11 cursor-pointer text-sm font-medium text-primary">
                  {t('location.monitoring.inventory')} ({devices.length})
                </summary>
                <ul className="divide-y divide-divider">
                  {devices.map((device, index) => (
                    <li
                      key={string(device.source_id) || index}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="break-words text-sm text-text">
                          {string(device.name) ||
                            t('location.monitoring.device')}
                        </p>
                        <p className="text-xs text-text-muted">
                          {string(device.kind)}
                          {string(device.observed_at)
                            ? ` · ${formatDateTime(string(device.observed_at))}`
                            : ''}
                        </p>
                      </div>
                      <StatusBadge
                        status={string(device.status) || 'unknown'}
                      />
                    </li>
                  ))}
                </ul>
              </details>
              {incidents.length > 0 && (
                <div className="mt-3 border-t border-border pt-3">
                  <h4 className="text-sm font-medium text-text">
                    {t('location.monitoring.incidents')}
                  </h4>
                  <ul className="mt-2 divide-y divide-divider">
                    {incidents.map((incident, index) => (
                      <li
                        key={string(incident.source_id) || index}
                        className="flex items-start justify-between gap-3 py-2"
                      >
                        <div>
                          <p className="text-sm text-text">
                            {string(incident.title) ||
                              t('location.monitoring.incident')}
                          </p>
                          <p className="text-xs text-text-muted">
                            {string(incident.occurred_at)
                              ? formatDateTime(string(incident.occurred_at))
                              : '—'}
                          </p>
                        </div>
                        <StatusBadge
                          status={string(incident.severity) || 'unknown'}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          );
        })
      )}
    </section>
  );
}

function Count({ title, value }: { title: string; value: number }) {
  return (
    <div>
      <dt className="text-xs text-text-faint">{title}</dt>
      <dd className="mt-1 text-lg font-semibold text-text">{value}</dd>
    </div>
  );
}
